import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route } from "react-router-dom";
import { Helmet } from "react-helmet";

import "./App.css";
import styles from "./App.module.css";

import useLocation from "./components/atoms/useLocation";
import Header from "./components/Header";
import { Work, Team, About } from "./components/Section";
import Login from "./components/atoms/Login";
import useDims from "./components/atoms/useDims";
import { hasTouch, getLanguage, checkIsPC } from "./components/atoms";

import { ENDPOINT_WORKS, ENDPOINT_ABOUT, ENDPOINT_TEAMS } from "./constants";
import AnimatedSection from "./components/AnimateSection";

// TODO: https://developers.google.com/web/fundamentals/performance/optimizing-content-efficiency/replace-animated-gifs-with-video/
// webm > mp4 > gif

const FontFix = () => {
  const isDev = process.env.REACT_APP_DEV ? true : false;
  console.log("FontFix:isDev:", isDev);

  return isDev ? (
    <Helmet>
      <base href="/" />
      <script type="text/javascript">var customPath = "/fonts";</script>
      <script type="text/javascript" src="/fonts/th-disco-webfonts.js" />
    </Helmet>
  ) : (
    <Helmet>
      <base href="/build/" />
      <script type="text/javascript">var customPath = "/build/fonts";</script>
      <script type="text/javascript" src="/build/fonts/th-disco-webfonts.js" />
    </Helmet>
  );
};

const getContainerDims = (currentPath, section, dims) => {
  const sections = [ENDPOINT_WORKS, ENDPOINT_TEAMS, ENDPOINT_ABOUT];
  const sectionWidth = (current, sectionType) => {
    if (sectionType === current) {
      return dims.width * 0.66; // active section
    } else if (sectionType !== current && sections.indexOf(current) !== -1) {
      return dims.width * 0.17; // other sections while one section is active
    } else {
      return dims.width * 0.33; // neutral
    }
  };
  const sectionHeight = (current, sectionType) => {
    if (sectionType === current) {
      return dims.height - 66 * 2 - 44;
    } else if (sectionType !== current && sections.indexOf(current) !== -1) {
      return 66;
    } else {
      return dims.height * 0.33;
    }
  };

  const isPC = checkIsPC(dims.width);

  return {
    height: isPC ? "" : sectionHeight(currentPath, section),
    width: isPC ? sectionWidth(currentPath, section) : "100%"
  };
};

const Content = ({ location, history, match, ...rest }) => {
  // language state
  const [lang, setLang] = useState(
    getLanguage(
      window.navigator.userLanguage || window.navigator.language || "en"
    )
  );

  // main page routing logic
  // GET /works has additional routing logic
  const [current, setCurrent] = useLocation(
    location,
    history,
    [ENDPOINT_ABOUT, ENDPOINT_TEAMS, ENDPOINT_WORKS],
    1,
    true
  );
  useEffect(() => {
    // match browser URL with current URL path
    if (current === "") {
      setCurrent(current);
    }
  }, [current, history]);

  const [scrolling, setScrolling] = useState(false);
  const handleScrolling = e => setScrolling(e);

  const { height, width } = useDims({
    height: window.innerHeight,
    width: window.innerWidth
  });

  const [isShowingModal, setIsShowingModal] = useState(false);

  return (
    <div
      className={`${styles.page} ${hasTouch() && styles.hasTouch}`}
      style={{ height: height }} // js height avoids navbar
    >
      <Header
        className={styles.header}
        onSelect={setCurrent}
        onLang={setLang}
        lang={lang}
        history={history}
      />

      <AnimatedSection current={current} showingModal={isShowingModal} />

      <div className={styles.responsiveSectionContainer}>
        <Work
          title="Work"
          endpoint={ENDPOINT_WORKS}
          className={`${styles.work} ${current === ENDPOINT_WORKS &&
            styles.active}`}
          dims={{
            ...getContainerDims(current, ENDPOINT_WORKS, { height, width })
          }}
          titleClassName={styles.workTitle}
          active={current === ENDPOINT_WORKS}
          current={current}
          lang={lang}
          onScroll={handleScrolling}
          scrolling={scrolling}
          onModal={setIsShowingModal}
          history={history}
        />

        <Team
          title="Team"
          endpoint={ENDPOINT_TEAMS}
          className={`${styles.team} ${current === ENDPOINT_TEAMS &&
            styles.active}`}
          dims={{
            ...getContainerDims(current, ENDPOINT_TEAMS, { height, width })
          }}
          active={current === ENDPOINT_TEAMS}
          current={current}
          onScroll={handleScrolling}
          scrolling={scrolling}
          workTabShowingModal={isShowingModal}
          history={history}
        />

        <About
          title="About"
          endpoint={ENDPOINT_ABOUT}
          className={`${styles.about} ${current === ENDPOINT_ABOUT &&
            styles.active}`}
          dims={{
            ...getContainerDims(current, ENDPOINT_ABOUT, { height, width })
          }}
          active={current === ENDPOINT_ABOUT}
          current={current}
          lang={lang}
          onScroll={handleScrolling}
          scrolling={scrolling}
          workTabShowingModal={isShowingModal}
          history={history}
        />
      </div>
    </div>
  );
};

const App = () => {
  // const isDev = process.env.REACT_APP_DEV ? true : false;
  // console.log("isDev:", isDev);

  // const [isLoggedIn, setIsLoggedIn] = useState(isDev);
  // console.log("isLoggedIn:", isLoggedIn);
  // if (!isLoggedIn) {
  //   return <Login isAuthenticated={setIsLoggedIn} />;
  // }

  return (
    <Router>
      <FontFix />
      <Route component={Content} />
    </Router>
  );
};

export default App;
